import type { Message } from '../types';

// A "reveal round" (planning poker / hidden voting) is not stored as
// channel state - it's derived from special marker messages the owner
// posts into the timeline: type "round", value one of the actions below.
// Every client computes the current phase from the message list, so the
// state propagates over the same Cue-nudge + refetch path every message
// already uses. See backend/workflows/signal/create.wsl (which owner-gates
// type "round") and screens/chat/{MessagesScreen,ChatScreen}.
export const ROUND_TYPE = 'round';

export type RoundAction = 'open' | 'reveal' | 'hide' | 'close';
export type RoundPhase = 'idle' | 'collecting' | 'revealed';

export interface RoundState {
  phase: RoundPhase;
  // The "open" marker's message id - stable across reveal/hide within one
  // round. '' when idle.
  roundId: string;
  // ISO date of the "open" marker; '' when idle. Any non-marker message
  // dated at/after this belongs to the round.
  openedAt: string;
  // Distinct senders who have answered this round (by display name - the
  // client Message has no senderId). While collecting this is all the
  // owner sees; the values stay masked.
  answeredCount: number;
  answererNames: string[];
  // Whether the viewer has sent an answer this round.
  iAnswered: boolean;
}

const IDLE: RoundState = {
  phase: 'idle',
  roundId: '',
  openedAt: '',
  answeredCount: 0,
  answererNames: [],
  iAnswered: false,
};

function isLiveMarker(m: Message): boolean {
  return m.type === ROUND_TYPE && m.syncStatus !== 'failed';
}

// Compare by real timestamp, not string order - an ISO date with
// fractional seconds ("…05.500Z", web) and one without ("…05Z", the iOS
// ISO8601DateFormatter default) don't sort chronologically as strings, so
// same-second answers could leak past the mask.
function ts(iso: string): number {
  const n = Date.parse(iso);
  return Number.isNaN(n) ? 0 : n;
}

// Order-independent: sorts the markers by date itself rather than trusting
// the incoming list order (a refetch can briefly reorder near-simultaneous
// messages, which used to flip the phase back and forth).
export function deriveRound(messages: Message[]): RoundState {
  const markers = messages.filter(isLiveMarker).slice().sort((a, b) => ts(b.date) - ts(a.date)); // newest first
  if (markers.length === 0) return IDLE;

  const newestMarker = markers[0];
  const action = (newestMarker.value ?? '') as RoundAction;
  if (action === 'close') return IDLE;

  // The "open" this round belongs to = the newest "open" marker.
  const openMarker = action === 'open' ? newestMarker : markers.find((m) => m.value === 'open');
  if (!openMarker) return IDLE;

  // A "close" newer than that "open" ends the round.
  if (markers.some((m) => m.value === 'close' && ts(m.date) > ts(openMarker.date))) return IDLE;

  const openedAt = openMarker.date;
  const openedTs = ts(openedAt);
  const answerers = new Set<string>();
  let iAnswered = false;
  for (const m of messages) {
    if (m.type === ROUND_TYPE) continue;
    if (ts(m.date) < openedTs) continue;
    answerers.add(m.sender);
    if (m.isMine) iAnswered = true;
  }

  return {
    phase: action === 'reveal' ? 'revealed' : 'collecting',
    roundId: openMarker.id,
    openedAt,
    answeredCount: answerers.size,
    answererNames: [...answerers],
    iAnswered,
  };
}

// A held answer whose content the viewer must not see yet.
export function isMaskedAnswer(m: Message, round: RoundState): boolean {
  return (
    round.phase === 'collecting' &&
    m.type !== ROUND_TYPE &&
    !m.isMine &&
    ts(m.date) >= ts(round.openedAt)
  );
}

export function roundActionLabel(action: string | undefined): string {
  switch (action) {
    case 'open':
      return 'Round started';
    case 'reveal':
      return 'Answers revealed';
    case 'hide':
      return 'Answers hidden';
    case 'close':
      return 'Round ended';
    default:
      return 'Round';
  }
}
